import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    TableCell,
    TableRow,
    Box,
    Typography,
    Checkbox,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RerunIcon from "../../../../assets/iconComponents/RerunIcon";
import ApproveAndRunIcon from "../../../../assets/iconComponents/ApproveAndRunIcon";
import UpdateIcon from "@mui/icons-material/Update";
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PageService from "../../../../services/PageService";
import { useNotification } from "../../../../context/NotificationContext";
import { formatDate } from "../../../../utils/formatDate";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditTestCaseModal from "components/common/EditTestCaseModal";

const TestCasesListItem = ({
    testCase,
    checkedTestCases,
    handleCheckboxChange,
    handleOpenDeleteDialog,
    tabToggler,
    setTabToggler,
}) => {
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const isMenuOpen = Boolean(anchorEl);
    const publicId = testCase?.public_id || testCase?.publicId || "-";
    const normalizedStatus = String(testCase?.status || "").toUpperCase();
    const isDraft = normalizedStatus === "DRAFT";
    const isRunning = normalizedStatus === "RUNNING";
    const canRunTestCase = [
        "DRAFT",
        "FAILED",
        "COMPLETED",
        "INITIAL_FAILURE",
    ].includes(normalizedStatus);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const navigate = useNavigate();
    const showNotification = useNotification();

    const statusStyles = {
        RUNNING: { backgroundColor: "#4574D2", color: "#fff" },
        COMPLETED: { backgroundColor: "#198754", color: "#fff" },
        FAILED: { backgroundColor: "#D32F2F", color: "#fff" },
        INITIAL_FAILURE: { backgroundColor: "#9B1C1C", color: "#fff" }, // added
        Draft: { backgroundColor: "#6D6E6F", color: "#fff" },
        PENDING: { backgroundColor: "#F0AD4E", color: "#fff" },
    };

    const handleOpenTestCase = () => {
        if (anchorEl) return;
        navigate(`/test-case/${testCase.id}`);
    };

    const handleRunTestCase = async () => {
        try {
            await PageService.runTestCase(testCase?.id);
            setTabToggler(!tabToggler);
        } catch (error) {
            console.log(error);
        }
    };

    const handleRegenerateTestCase = async () => {
        showNotification(
            "Test case regenerating has started",
            "info",
            "Learn more",
            navigate
        );
        setIsRegenerating(true);
        try {
            await PageService.regenerateTestCasesById(testCase.id);
        } catch (error) {
            console.log(error);
            setIsRegenerating(false);
        }
    };

    return (
        <TableRow
            key={testCase.id}
            className="testCasesTableBodyRow"
            onClick={handleOpenTestCase}
        >
            <TableCell
                className="testCasesTableBodyCheckboxCell"
                onClick={(e) => e.stopPropagation()}
            >
                <Checkbox
                    size="small"
                    className="testCasesTableBodyCheckbox"
                    checked={checkedTestCases.includes(testCase.id)}
                    onChange={() => handleCheckboxChange(testCase.id)}
                />
            </TableCell>
            <TableCell className="testCasesTableBodyCell testCaseId">
                {publicId}
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {publicId}
                    </Typography>
                    <Typography>{testCase.name}</Typography>
                </Box>
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                {formatDate(testCase.created_at)}
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                {testCase.updated_at ? formatDate(testCase.updated_at) : "N/A"}
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                {testCase.created_by || "-"}
            </TableCell>
            <TableCell className="testCasesTableBodyCell">
                <Box className="testCasesTableBodyStatusContainer">
                    <Tooltip
                        title={
                            testCase.status === "INITIAL_FAILURE"
                                ? "A potential bug was found. Check details to generate negative tests."
                                : ""
                        }
                        disableHoverListener={
                            testCase.status !== "INITIAL_FAILURE"
                        }
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <Box
                            className="testCasesTableBodyStatus"
                            sx={{
                                ...statusStyles[testCase.status],
                                whiteSpace: "nowrap",
                                fontSize: "12px",
                                lineHeight: 1.2,
                                padding: "4px 10px",
                                maxWidth: "150px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {testCase.status === "RUNNING"
                                ? "In Progress..."
                                : testCase.status === "COMPLETED"
                                ? "Passed"
                                : testCase.status === "INITIAL_FAILURE"
                                ? "Initial failure"
                                : testCase.status.toLowerCase()}
                        </Box>
                    </Tooltip>
                    {testCase.status === "INITIAL_FAILURE" && (
                        <Tooltip
                            title="A potential bug was found. Check details to generate negative tests."
                            PopperProps={{
                                sx: {
                                    "& .MuiTooltip-tooltip": {
                                        backgroundColor: "primary.main",
                                    },
                                },
                            }}
                        >
                            <InfoOutlinedIcon
                                fontSize="small"
                                style={{
                                    marginLeft: 6,
                                    color: "#8E1515",
                                    cursor: "pointer",
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell className="testCasesTableBodyCell testCasesActions">
                {canRunTestCase && !isRegenerating ? (
                    <Tooltip
                        title={isDraft ? "Approve and Run" : "Re-Run"}
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <IconButton
                            className="testCasesActionsIconContainer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRunTestCase();
                            }}
                        >
                            {isDraft ? (
                                <ApproveAndRunIcon color="#2D4356" />
                            ) : (
                                <RerunIcon color="#2D4356" />
                            )}
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Box
                        className="testCasesActionsDisabledIconContainer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isDraft ? (
                            <ApproveAndRunIcon color="#2D4356" />
                        ) : (
                            <RerunIcon color="#2D4356" />
                        )}
                    </Box>
                )}
                <IconButton
                    className="testCasesActionsIconContainer"
                    onClick={handleMenuOpen}
                    disabled={isRegenerating || isRunning}
                >
                    <MoreVertIcon
                        color="primary"
                        sx={{
                            opacity: isRegenerating || isRunning ? 0.3 : 1,
                        }}
                    />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MenuItem
                        disabled={isRegenerating}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose();
                            handleRegenerateTestCase();
                        }}
                    >
                        <UpdateIcon sx={{ mr: 2 }} color="primary" />
                        Regenerate Test Case
                    </MenuItem>
                    <MenuItem
                        disabled={isRegenerating}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose();
                            setIsRenameModalOpen(true);
                        }}
                    >
                        <EditIcon sx={{ mr: 2 }} color="primary" />
                        Rename Test Case
                    </MenuItem>
                    <MenuItem
                        disabled={isRegenerating}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose();
                            handleOpenDeleteDialog(testCase?.id);
                        }}
                    >
                        <DeleteOutlineOutlinedIcon
                            sx={{ mr: 2 }}
                            color="error"
                        />
                        <Typography color="error">Delete Test Case</Typography>
                    </MenuItem>
                </Menu>
            </TableCell>
            {isRenameModalOpen && (
                <EditTestCaseModal
                    open={isRenameModalOpen}
                    onClose={() => setIsRenameModalOpen(false)}
                    testCase={testCase}
                    onSuccess={() => setTabToggler(!tabToggler)}
                />
            )}
        </TableRow>
    );
};

export default TestCasesListItem;
